import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Phase 4 PR 3.A — Huri staff roster.
 *
 * Super-admins can see every Huri-side user (super_admin + technician),
 * invite new technicians ahead of field work, edit name/email, and
 * deactivate / reactivate. Technicians invited here will pick a
 * password via the same 7-day invite-email flow that Managers use.
 */
function TeamPage({ currentUserId = null }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => {
    axios.get('/api/admin/staff')
      .then(res => setUsers(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינת הצוות'));
  };
  useEffect(load, []);

  const flash = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2400);
  };

  const onCreate = async (payload) => {
    await axios.post('/api/admin/staff', payload);
    setShowCreate(false);
    flash('נוצר משתמש ונשלחה אליו הזמנה במייל');
    load();
  };

  const onEditSave = async (userId, payload) => {
    await axios.patch(`/api/admin/staff/${userId}`, payload);
    setEditing(null);
    flash('הפרטים עודכנו');
    load();
  };

  const onDeactivate = async (u) => {
    if (!window.confirm(`להשבית את ${u.name}? המשתמש לא יוכל להתחבר עד שיופעל מחדש.`)) return;
    setBusyUserId(u.id);
    try {
      await axios.post(`/api/admin/staff/${u.id}/deactivate`);
      flash('המשתמש הושבת');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהשבתה');
    } finally {
      setBusyUserId(null);
    }
  };

  const onReactivate = async (u) => {
    setBusyUserId(u.id);
    try {
      await axios.post(`/api/admin/staff/${u.id}/reactivate`);
      flash('המשתמש הופעל מחדש');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהפעלה מחדש');
    } finally {
      setBusyUserId(null);
    }
  };

  const onHardDelete = async (u) => {
    const ok = window.confirm(
      `למחוק לצמיתות את ${u.name}?\n\n` +
      `המחיקה אינה הפיכה — כל הרשומות שלו (היסטוריה, ערוצי התראה) יוסרו ` +
      `והאימייל / טלפון יתפנו לשימוש חוזר.`
    );
    if (!ok) return;
    setBusyUserId(u.id);
    try {
      await axios.delete(`/api/admin/staff/${u.id}`);
      flash('המשתמש נמחק לצמיתות');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במחיקה');
    } finally {
      setBusyUserId(null);
    }
  };

  const onResendInvite = async (u) => {
    setBusyUserId(u.id);
    try {
      await axios.post(`/api/admin/staff/${u.id}/resend-invite`);
      flash('הזמנה חדשה נשלחה במייל');
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשליחת ההזמנה');
    } finally {
      setBusyUserId(null);
    }
  };

  if (users === null && !error) return <div className="loading">טוען...</div>;

  return (
    <>
      <div className="breadcrumb">
        <span>צוות חורי</span>
      </div>

      <div className="section-header">
        <h3>צוות חורי</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          + הזמן טכנאי
        </button>
      </div>

      <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
        טכנאים שתזמינו כאן יקבלו מייל עם קישור לקביעת סיסמה. אחרי כניסה הם יראו את אפליקציית הטכנאי הייעודית להתקנות.
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', padding: '0.6rem 0.85rem',
          borderRadius: '6px', marginBottom: '0.85rem', fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>שם</th>
              <th>אימייל</th>
              <th>תפקיד</th>
              <th>כניסה אחרונה</th>
              <th>סטטוס</th>
              <th style={{ textAlign: 'left' }}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map(u => {
              const isSelf = currentUserId === u.id;
              const inactive = !u.is_active;
              return (
                <tr key={u.id} style={inactive ? { opacity: 0.55 } : undefined}>
                  <td>
                    {u.name}
                    {isSelf && (
                      <span style={{ marginRight: '0.4rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                        (אתה)
                      </span>
                    )}
                  </td>
                  <td>{u.email || '-'}</td>
                  <td>{u.role === 'super_admin' ? 'סופר אדמין' : 'טכנאי'}</td>
                  <td>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString('he-IL', {
                          timeZone: 'Asia/Jerusalem',
                          year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : <span style={{ color: '#9ca3af' }}>טרם נכנס</span>}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.15rem 0.6rem',
                      borderRadius: '999px',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      background: inactive ? '#fef2f2' : '#ecfdf5',
                      color: inactive ? '#b91c1c' : '#047857',
                    }}>
                      {inactive ? 'מושבת' : 'פעיל'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    <RowActions
                      user={u}
                      isSelf={isSelf}
                      busy={busyUserId === u.id}
                      onEdit={() => setEditing(u)}
                      onDeactivate={() => onDeactivate(u)}
                      onReactivate={() => onReactivate(u)}
                      onResendInvite={() => onResendInvite(u)}
                      onHardDelete={() => onHardDelete(u)}
                    />
                  </td>
                </tr>
              );
            })}
            {users && users.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                  אין עדיין אנשי צוות. הזמינו טכנאי עם הכפתור למעלה.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <StaffModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSubmit={onCreate}
        />
      )}
      {editing && (
        <StaffModal
          mode="edit"
          user={editing}
          onClose={() => setEditing(null)}
          onSubmit={(payload) => onEditSave(editing.id, payload)}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a2e', color: '#fff',
          padding: '0.6rem 1.1rem', borderRadius: '6px',
          fontSize: '0.85rem', zIndex: 200,
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}
    </>
  );
}


function RowActions({ user, isSelf, busy, onEdit, onDeactivate, onReactivate, onResendInvite, onHardDelete }) {
  const baseBtn = {
    padding: '0.25rem 0.6rem',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#1a1a2e',
    borderRadius: '5px',
    fontSize: '0.78rem',
    cursor: busy ? 'wait' : 'pointer',
    fontFamily: 'Heebo, sans-serif',
    marginInlineStart: '0.35rem',
  };
  const dangerBtn = { ...baseBtn, background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' };

  return (
    <span>
      <button style={baseBtn} disabled={busy} onClick={onEdit}>ערוך</button>
      {!user.is_active ? (
        <>
          <button style={baseBtn} disabled={busy} onClick={onReactivate}>הפעל מחדש</button>
          {!isSelf && (
            <button style={dangerBtn} disabled={busy} onClick={onHardDelete}>
              מחק לצמיתות
            </button>
          )}
        </>
      ) : (
        <>
          {!user.has_logged_in && (
            <button style={baseBtn} disabled={busy} onClick={onResendInvite}>
              שלח שוב הזמנה
            </button>
          )}
          {!isSelf && (
            <button style={dangerBtn} disabled={busy} onClick={onDeactivate}>
              השבת
            </button>
          )}
        </>
      )}
    </span>
  );
}


function StaffModal({ mode, user, onClose, onSubmit }) {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [role, setRole] = useState('technician');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (mode === 'create') {
        await onSubmit({ name, email, phone, role });
      } else {
        await onSubmit({ name, email, phone });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="modal-overlay" onClick={onClose} />
      <form
        className="modal"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(480px, calc(100vw - 24px))',
        }}
        onSubmit={handleSubmit}
      >
        <h2>{mode === 'create' ? 'הזמנת איש צוות' : 'עריכת איש צוות'}</h2>
        <div className="form-group">
          <label>שם מלא</label>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus />
        </div>
        <div className="form-group">
          <label>אימייל</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            dir="ltr"
            style={{ textAlign: 'left' }}
          />
        </div>
        <div className="form-group">
          <label>
            טלפון (לכניסה בוואטסאפ)
            {mode === 'edit' && user?.phone && (
              <span style={{
                marginRight: '0.4rem',
                fontSize: '0.72rem',
                fontWeight: 500,
                color: user.phone_verified ? '#047857' : '#92400e',
              }}>
                {user.phone_verified ? '• מאומת' : '• טרם אומת'}
              </span>
            )}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="05X-XXXXXXX"
            dir="ltr"
            style={{ textAlign: 'left' }}
          />
        </div>
        {mode === 'create' && (
          <>
            <div className="form-group">
              <label>תפקיד</label>
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                style={{
                  width: '100%', padding: '0.55rem 0.75rem',
                  border: '1px solid #e5e7eb', borderRadius: '6px',
                  fontSize: '0.9rem', fontFamily: 'Heebo, sans-serif',
                }}
              >
                <option value="technician">טכנאי</option>
                <option value="super_admin">סופר אדמין</option>
              </select>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.4rem 0 0' }}>
              המשתמש יקבל מייל עם קישור לקביעת סיסמה והתחברות.
            </p>
          </>
        )}
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#b91c1c', padding: '0.5rem 0.75rem',
            borderRadius: '6px', marginTop: '0.85rem', fontSize: '0.8rem',
          }}>
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'שומר...' : mode === 'create' ? 'שלח הזמנה' : 'שמירה'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            ביטול
          </button>
        </div>
      </form>
    </>,
    document.body
  );
}


export default TeamPage;
